import { ApolloProvider } from '@apollo/client';
import { Provider } from 'react-redux';
import { merge, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import envConfig from 'build-and-quote/config/index.js';
import {
    ConfigsModel,
    ConfigsService as GeneralConfigService,
    ContentZonesService,
    LocaleService,
    UrlService,
    WindowProxy,
} from '@sm360/widget-commons-core-services';
import {
    EVENTS,
    HistoryService,
    ApolloClient,
    ReduxClient,
    VehiclesService,
    LocalStorageService,
    AdobeService,
    ADOBE_TAGS,
    APR,
    AprService,
    SessionStorageService,
} from '@sm360/widget-commons-general';
import {
    PurchaseMethods,
    PurchaseMethodsV25,
    PaymentOptionsMetadata,
    Vehicle,
    ModelYear,
    Quote,
    ShowroomService,
    VehicleConstants,
    // UNIT_LOCATION,
} from '@sm360/widget-commons-vehicle';
import PaymentCalculator from '@sm360/widget-payment-calculator';
import PaymentCalculatorV25 from '@sm360/widget-payment-calculator-v25';
import TradeIn from '@sm360/widget-trade-in';

import { loadModelYear, loadModelYears } from 'build-and-quote/actions/buildAndQuoteActions.js';

import BuildAndQuote from 'build-and-quote/components/BuildAndQuote/BuildAndQuote.jsx';
import ConfigsService from 'build-and-quote/services/ConfigsService/ConfigsService.js';

import en from 'build-and-quote/i18n/en.json';
import fr from 'build-and-quote/i18n/fr.json';

import buildAndQuoteReducer from 'build-and-quote/reducers/buildAndQuoteReducer.js';
import freightLabelReducer from 'build-and-quote/reducers/freightLabelReducer.js';

const { TradeInVehicleActions } = Vehicle;
const { PurchaseMethodsActionsV25 } = PurchaseMethodsV25;
const { PaymentOptionsMetadataActions, PaymentOptionsMetadataReducer } = PaymentOptionsMetadata;

class BuildAndQuoteProvider extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            vehicle: [],
        };

        merge(
            en,
            PurchaseMethods.Translations.en,
            Vehicle.Translation.en,
            PaymentCalculator.translations.en,
            PaymentCalculatorV25.translations.en,
            ModelYear.Translations.en,
            Quote.Translation.en,
            TradeIn.Translations.en
        );
        merge(
            fr,
            PurchaseMethods.Translations.fr,
            Vehicle.Translation.fr,
            PaymentCalculator.translations.fr,
            PaymentCalculatorV25.translations.fr,
            ModelYear.Translations.fr,
            Quote.Translation.fr,
            TradeIn.Translations.fr
        );

        LocaleService.setLocale(props.locale);
        LocaleService.loadTranslations({ en, fr });

        this.configsModel = new ConfigsModel();

        GeneralConfigService.setLocation(props.location);
        GeneralConfigService.setWebsiteId(props.websiteId);
        GeneralConfigService.setOrganizationId(props.organizationId);
        GeneralConfigService.setOrganizationUnitId(props.organizationUnitId);
        GeneralConfigService.setOrganizationUnitLocation(props.organizationUnitLocation);
        GeneralConfigService.setIsInMobileApp(props.isInMobileApp);

        ContentZonesService.setImageServerBaseUrl(envConfig.source.image);
        ContentZonesService.addContentZones(props.contentZones);

        this.storeRedux = ReduxClient.getOrCreate();

        ReduxClient.addReducers({
            buildAndQuote: buildAndQuoteReducer,
            modelYear: ModelYear.ModelYearReducer,
            purchaseMethods: PurchaseMethods.PurchaseMethodsReducer,
            quote: Quote.QuoteReducer,
            tradeInVehicle: Vehicle.TradeInVehicleReducer,
            freightLabel: freightLabelReducer,
            // Reducers for V2.5 migration
            purchaseMethodsV25: PurchaseMethodsV25.PurchaseMethodsReducerV25,
            vehicleV25: Vehicle.VehicleReducerV25,
            quoteV25: Quote.QuoteReducerV25,
            paymentOptionsMetadata: PaymentOptionsMetadataReducer,
        });

        this.onTradeInCreated = this.onTradeInCreated.bind(this);
        this.checkTradeInVehicleStatus = this.checkTradeInVehicleStatus.bind(this);
        this.loadQuote = this.loadQuote.bind(this);
        this.loadVehicle = this.loadVehicle.bind(this);
        this.onUnload = this.onUnload.bind(this);
        this.setConfigData = this.setConfigData.bind(this);
    }

    componentWillMount() {
        ConfigsService.setCatalogAvailability(this.props.isCatalogAvailable);

        const urlParamQuoteId = UrlService.getUrlParameter('quoteId');

        let vehicleId = UrlService.getUrlParameter('vehicleId');
        SessionStorageService.set('dealerMake', this.props?.dealerMake || '');
        if (!vehicleId) {
            const windowLocation = WindowProxy.location();
            const pathPart = (windowLocation && windowLocation.pathname.split('/').pop()) || '';
            const urlVehicleId = pathPart.split('-').pop();

            if (urlVehicleId.indexOf('id') === 0) {
                vehicleId = urlVehicleId.replace('id', '');
            }
        }

        // All the possible conditions for when loading this widget
        // vehicleId in URL but no quoteId in URL
        // vehicleId and quoteId exists in URL
        // no vehicleId and no quoteId exist in URL = Builder
        // quoteId and no vehicleId in URL and quote contains vehicleId
        // quoteId and no vehicleId in URL but quote does not contain a vehicleId

        const tradeIn = parseInt(LocalStorageService.get('tradeIn') || 0, 10);
        const lien = parseInt(LocalStorageService.get('lien') || 0, 10);
        const cashDown = parseInt(LocalStorageService.get('cashDown') || 0, 10);

        if (vehicleId) {
            if (urlParamQuoteId) {
                this.loadQuote(urlParamQuoteId);
            } else {
                this.loadVehicle(vehicleId, tradeIn, lien, cashDown).then((vehicleModel) => {
                    const saveNewQuoteModelV25 = new Quote.SaveNewQuoteModelV25();
                    const addToGarageV25 = new Quote.AddToGarageV25();

                    return addToGarageV25
                        .createPublicQuote(saveNewQuoteModelV25.setModelValuesFromVehicleModelV25(vehicleModel))
                        .then((quoteId) => {
                            this.loadQuote(quoteId).then(() => {
                                const state = this.storeRedux.getState();

                                const modelYearsParams = {
                                    year: state.quote?.vehicle?.year,
                                    make: state.quote?.vehicle?.make,
                                    model: state.quote?.vehicle?.model,
                                    bodyStyleCode: state?.quote?.vehicle?.bodyStyleCode,
                                    modelTrimCode: vehicleModel.vehicleInventoryBuild.modelTrimCodeManufacturer,
                                };

                                if (vehicleModel.getIsVehicleNew()) {
                                    modelYearsParams.vehicleConditionType = vehicleModel.demo
                                        ? VehicleConstants.CONDITIONS.DEMO
                                        : VehicleConstants.CONDITIONS.NEW;
                                } else {
                                    modelYearsParams.vehicleConditionType = vehicleModel.certified
                                        ? VehicleConstants.CONDITIONS.CERTIFIED
                                        : VehicleConstants.CONDITIONS.USED;
                                }

                                const tradeInVehicleParams = {
                                    id: '',
                                };

                                if (
                                    !isEmpty(modelYearsParams.make) &&
                                    !isEmpty(modelYearsParams.model) &&
                                    !isEmpty(modelYearsParams.bodyStyleCode)
                                ) {
                                    this.storeRedux.dispatch(
                                        loadModelYear(modelYearsParams, tradeInVehicleParams, quoteId, true) // last param is for unfiltered for VDP only
                                    );
                                }

                                this.setState({
                                    vehicle: vehicleModel,
                                });
                            });
                        });
                });
            }
        } else if (!vehicleId && urlParamQuoteId) {
            this.loadQuote(urlParamQuoteId);
        } else {
            const modelYearsParams = {
                year: this.props.year,
                make: this.props.make,
                model: this.props.model,
                bodyStyleCode: this.props.bodyStyleCodeId,
            };

            const tradeInVehicleParams = {
                id: '',
            };
            this.setConfigData();
            if (
                !isEmpty(modelYearsParams.make) &&
                !isEmpty(modelYearsParams.model) &&
                !isEmpty(modelYearsParams.bodyStyleCode)
            ) {
                this.storeRedux.dispatch(loadModelYears(modelYearsParams, tradeInVehicleParams, true));
            }
        }

        const freightLabel = ContentZonesService.getContentZone(
            'widget.build.and.buy',
            'widget.build.and.buy.freight.label'
        )
            ? ContentZonesService.getContentZone('widget.build.and.buy', 'widget.build.and.buy.freight.label').getText()
            : null;

        this.storeRedux.dispatch({
            type: 'SET_FREIGHT_LABEL',
            payload: freightLabel,
        });
    }

    componentDidMount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.addEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);
            $window.addEventListener('beforeunload', this.onUnload);
        }
    }

    componentWillUnmount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.removeEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);
            this.onUnload();
        }
    }

    onUnload() {
        HistoryService.add(WindowProxy.location().href);
    }

    onTradeInCreated() {
        this.checkTradeInVehicleStatus(true);
    }

    setConfigData(vehicleOrgUnitId = '') {
        this.configsModel = new ConfigsModel({
            organizationUnitId: vehicleOrgUnitId || this.props?.organizationUnitId,
            organizationUnitLocation: this.props?.organizationUnitLocation,
            websiteLogo: this.props?.websiteLogo,
            websiteDomain: this.props?.websiteDomain,
            organizationUnitPhone: this.props?.organizationUnitPhone,
            websiteFacebookPageUrl: this.props?.websiteFacebookPageUrl,
            websiteInstagramClientId: this.props?.websiteInstagramClientId,
            websiteInstagramUserId: this.props?.websiteInstagramUserId,
            websiteTwitterUrl: this.props?.websiteTwitterUrl,
            websiteYoutubeUrl: this.props?.websiteYoutubeUrl,
            organizationUnitName: this.props?.organizationUnitName,
            dealerMake: this.props?.dealerMake,
        });
    }

    loadVehicle(vehicleId, tradeIn = 0, lien = 0, cashDown = 0, quoteId) {
        return VehiclesService.loadVehicle(
            vehicleId,
            'new',
            {
                location: this.props.organizationUnitLocation,
                orgId: this.props.organizationId,
                orgUnitId: this.props.organizationUnitId,
            },
            this.props.locale,
            { tradeIn, lien, cashDown, quoteId }
        ).then(({ vehicle, paymentOptionsMetadata }) => {
            const vehicleModel = new Vehicle.VehicleModelV25(vehicle);

            // Initialize purchaseMethodsV25 in Redux store
            this.storeRedux.dispatch(PurchaseMethodsActionsV25.loadPurchaseMethodsSuccess(vehicle.paymentOptions));

            // Initialize purchase method metadatas
            const metaData = paymentOptionsMetadata.map((Data) => {
                return new PaymentOptionsMetadata.PaymentOptionsMetadataModel({
                    ...Data,
                });
            });

            this.storeRedux.dispatch(PaymentOptionsMetadataActions.loadPaymentOptionsMetadata(metaData));

            this.setConfigData(vehicleModel.getOrgUnitId());

            if (this.props.hasAdobeTagging && !this.hasLoaded) {
                AdobeService.eventTrigger(
                    ADOBE_TAGS.VDP_LOAD(vehicleModel.bodyStyle?.name, {
                        make: vehicleModel?.make?.name,
                        model: vehicleModel?.model?.name,
                    }),
                    'si-details-load'
                );
                this.hasLoaded = true;
            }

            return Promise.resolve(vehicleModel);
        });
    }

    loadQuote(quoteId) {
        ShowroomService.searchQuoteV25(quoteId).then((quoteV25) => {
            this.storeRedux.dispatch(Quote.QuoteActions.setQuoteV25(quoteV25));
        });

        ShowroomService.loadQuote(quoteId).then((quote) => {
            this.storeRedux.dispatch(Quote.QuoteActions.setQuote(quote.set('modified', false)));

            if (quote.vehicleId) {
                this.loadVehicle(quote.vehicleId, quote.tradeIn, quote.lien, quote.cashDown, quoteId).then(
                    (vehicleModel) => {
                        const modelYearsParams = {
                            year: quote.vehicle.year,
                            make: quote.vehicle.make,
                            model: quote.vehicle.model,
                            bodyStyleCode: quote.vehicle.bodyStyleCode,
                            modelTrimCode: quote.vehicle.modelTrimCode,
                            vehicleConditionType: quote.vehicle.vehicleConditionType,
                        };

                        const tradeInVehicleParams = {
                            id: '',
                        };

                        this.storeRedux.dispatch(loadModelYear(modelYearsParams, tradeInVehicleParams, quoteId, true)); // last param is for unfiltered for VDP only

                        this.setState({
                            vehicle: vehicleModel,
                        });
                    }
                );
            } else {
                const modelYearsParams = {
                    year: quote.vehicle.year,
                    make: quote.vehicle.make,
                    model: quote.vehicle.model,
                    bodyStyleCode: quote.vehicle.bodyStyleCode,
                    modelTrimCode: quote.vehicle.modelTrimCode,
                    vehicleConditionType: quote.vehicle.vehicleConditionType,
                };
                const tradeInVehicleParams = {
                    id: '',
                };
                this.storeRedux.dispatch(loadModelYear(modelYearsParams, tradeInVehicleParams, quoteId, true)); // last param is for unfiltered for VDP only
                this.setConfigData();

                this.setState({
                    vehicle: quote.vehicle,
                });
            }
        });
    }

    checkTradeInVehicleStatus(forceLoad) {
        const storeState = this.storeRedux.getState();
        if (forceLoad || isEmpty(storeState.tradeInVehicle) || !storeState.tradeInVehicle.id) {
            this.storeRedux.dispatch(TradeInVehicleActions.loadTradeInVehicle(storeState.tradeInVehicle));
        }
    }

    isNewVehicle(newProps, oldProps) {
        return (
            newProps.year !== oldProps.year ||
            newProps.make !== oldProps.make ||
            newProps.model !== oldProps.model ||
            newProps.bodyStyleCodeId !== oldProps.bodyStyleCodeId
        );
    }

    render() {
        const config = this.configsModel.getConfig();
        const orgUnitId = config?.orgUnitId || GeneralConfigService.getOrganizationUnitId();
        const location = config?.location || GeneralConfigService?.getOrganizationUnitLocation();
        const isManitoba = APR.APR_LOCATIONS.includes(location);
        const displayCreditRate = AprService.displayApr(location, orgUnitId);

        return (
            <ApolloProvider client={ApolloClient.getOrCreate()}>
                <Provider store={this.storeRedux}>
                    <BuildAndQuote
                        vehicle={this.state.vehicle}
                        isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                        websiteSoldDaysShown={this.props.websiteSoldDaysShown}
                        organizationId={this.props.organizationId}
                        config={config}
                        hasAdobeTagging={this.props.hasAdobeTagging}
                        displayCreditRate={displayCreditRate}
                        displayFactoryOrder={isManitoba}
                        motoInsightNewEnabled={this.props.motoInsightNewEnabled}
                        roadsterNewEnabled={this.props.roadsterNewEnabled}
                        eshopNewEnabled={this.props.eshopNewEnabled}
                        shopClickDriveNewEnabled={this.props.shopClickDriveNewEnabled}
                        roadsterLink={this.props.roadsterLink}
                        dealerCode={this.props.dealerCode}
                        dealerBAC={this.props.dealerBAC}
                        noMakeListStr={this.props.noMakeListStr}
                        modelListStr={this.props.modelListStr}
                    />
                </Provider>
            </ApolloProvider>
        );
    }
}

BuildAndQuoteProvider.propTypes = {
    // bodyStyleCodeId: PropTypes.string.isRequired,
    contentZones: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                active: PropTypes.bool,
                group: PropTypes.string,
                id: PropTypes.number,
                location: PropTypes.string,
                position: PropTypes.number,
                type: PropTypes.string,
                value: PropTypes.string,
            })
        )
    ).isRequired,
    isCatalogAvailable: PropTypes.bool.isRequired,
    isInMobileApp: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    modelTrimCodeId: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    organizationId: PropTypes.number.isRequired,
    organizationUnitId: PropTypes.number.isRequired,
    organizationUnitLocation: PropTypes.string.isRequired,
    websiteId: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
    isShowroomV2Enabled: PropTypes.bool.isRequired,
    websiteSoldDaysShown: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    hasAdobeTagging: PropTypes.bool,
    motoInsightNewEnabled: PropTypes.bool,
    roadsterNewEnabled: PropTypes.bool,
    eshopNewEnabled: PropTypes.bool,
    shopClickDriveNewEnabled: PropTypes.bool,
    roadsterLink: PropTypes.string,
    dealerCode: PropTypes.string,
    dealerBAC: PropTypes.string,
    dealerMake: PropTypes.string,
    noMakeListStr: PropTypes.string,
    modelListStr: PropTypes.string,
};

BuildAndQuoteProvider.defaultProps = {
    isInMobileApp: false,
    websiteSoldDaysShown: 0,
    hasAdobeTagging: false,
    motoInsightNewEnabled: false,
    roadsterNewEnabled: false,
    eshopNewEnabled: false,
    shopClickDriveNewEnabled: false,
    roadsterLink: '',
    dealerCode: '',
    dealerBAC: '',
    dealerMake: '',
    noMakeListStr: '',
    modelListStr: '',
};

BuildAndQuoteProvider.displayName = 'BuildAndQuoteProvider';

export default BuildAndQuoteProvider;
